import { useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Navigation } from "lucide-react";
import L from "leaflet";
import "@/lib/leaflet-setup";
import { OrderDetailData } from "@/types/orders";

interface OrderCemeteryCardProps {
  cemetery: OrderDetailData["cemeteries"];
}

export function OrderCemeteryCard({ cemetery }: OrderCemeteryCardProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!cemetery?.latitude || !cemetery?.longitude || !mapRef.current) return;
    if (mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      dragging: true,
      zoomControl: true,
    }).setView([cemetery.latitude, cemetery.longitude], 15);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    L.marker([cemetery.latitude, cemetery.longitude]).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [cemetery?.latitude, cemetery?.longitude]);

  const openGoogleMaps = () => {
    if (cemetery?.latitude && cemetery?.longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${cemetery.latitude},${cemetery.longitude}`;
      window.open(url, "_blank");
    }
  };

  if (!cemetery) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Hautausmaa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Ei hautausmaata valittu</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Hautausmaa
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="font-medium">{cemetery.name}</p>
          <p className="text-sm text-muted-foreground">
            {cemetery.address}, {cemetery.postal_code} {cemetery.city}
          </p>
        </div>

        {cemetery.latitude && cemetery.longitude && (
          <>
            <div
              ref={mapRef}
              className="h-[200px] rounded-md overflow-hidden border isolate"
            />
            <Button variant="outline" size="sm" onClick={openGoogleMaps} className="w-full">
              <Navigation className="h-4 w-4 mr-2" />
              Avaa reittiohjeet
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
